import { Injectable, inject } from '@angular/core';
import { Firestore, Timestamp, doc, setDoc, serverTimestamp } from '@angular/fire/firestore';
import { Observable, map } from 'rxjs';
import { FirebaseAwareService } from '../shared/firestore/firebase-aware.service';
import { fromDocSnapshot } from '../shared/firestore/live-query';

export type AppPhase = 'landing' | 'maintenance' | 'production';

export interface AppConfig {
  appPhase: AppPhase;
  landingExitedAt: Timestamp | null;
  maintenanceMessage: string;
  maintenancePlannedEnd: Timestamp | null;
}

const CONFIG_DOC = 'config/app';

const DEFAULTS: AppConfig = {
  appPhase: 'production',
  landingExitedAt: null,
  maintenanceMessage: 'Estamos realizando mejoras. Volvemos pronto.',
  maintenancePlannedEnd: null,
};

@Injectable({ providedIn: 'root' })
export class AppConfigService extends FirebaseAwareService {
  private readonly firestore = inject(Firestore);

  /** Always emits a value (defaults if `/config/app` missing) so guards never hang. */
  readonly config$: Observable<AppConfig> = this.inCtx(() =>
    fromDocSnapshot<AppConfig>(
      this.injector,
      doc(this.firestore, CONFIG_DOC),
      snap => {
        if (!snap.exists()) return { ...DEFAULTS };
        const data = snap.data() as Partial<AppConfig>;
        return {
          ...DEFAULTS,
          ...data,
          appPhase: (data.appPhase as AppPhase | undefined) ?? DEFAULTS.appPhase,
        };
      },
    ),
  ).pipe(map(c => c ?? { ...DEFAULTS }));

  async setPhase(phase: AppPhase, currentConfig: AppConfig): Promise<void> {
    const ref = this.inCtx(() => doc(this.firestore, CONFIG_DOC));
    const isExitingLanding = currentConfig.appPhase === 'landing' && phase !== 'landing';
    await this.inCtx(() =>
      setDoc(ref, {
        ...currentConfig,
        appPhase: phase,
        landingExitedAt: isExitingLanding ? serverTimestamp() : currentConfig.landingExitedAt,
      }, { merge: true }),
    );
  }

  async updateMaintenanceMessage(msg: string, plannedEnd: Date | null, currentConfig: AppConfig): Promise<void> {
    const ref = this.inCtx(() => doc(this.firestore, CONFIG_DOC));
    await this.inCtx(() =>
      setDoc(ref, {
        ...currentConfig,
        maintenanceMessage: msg,
        maintenancePlannedEnd: plannedEnd ? Timestamp.fromDate(plannedEnd) : null,
      }, { merge: true }),
    );
  }

  getDefaults(): AppConfig {
    return { ...DEFAULTS };
  }
}
